import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

export const accountAuthEventName = 'account-auth-changed';

const dispatchAccountAuthChange = () => {
  window.dispatchEvent(new Event(accountAuthEventName));
};

const getUserPool = (): CognitoUserPool => {
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  if (!userPoolId || !clientId) {
    throw new Error('Cognito env vars not configured');
  }
  return new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId });
};

const getCognitoUser = (email: string): CognitoUser => {
  return new CognitoUser({ Username: email, Pool: getUserPool() });
};

export const accountSignUp = (email: string, password: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    getUserPool().signUp(email, password, [], [], (err) => {
      if (err) reject(err);
      else resolve();
    });
  });
};

export const accountConfirmSignUp = (email: string, code: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    getCognitoUser(email).confirmRegistration(code, true, (err) => {
      if (err) reject(err);
      else resolve();
    });
  });
};

export const accountSignIn = (email: string, password: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });
    getCognitoUser(email).authenticateUser(authDetails, {
      onSuccess: () => {
        dispatchAccountAuthChange();
        resolve();
      },
      onFailure: (err) => reject(err),
    });
  });
};

export const accountSignOut = (): void => {
  try {
    const pool = getUserPool();
    const user = pool.getCurrentUser();
    if (user) {
      user.signOut();
    }
  } catch {
    // ignore
  }
  dispatchAccountAuthChange();
};

export const getAccountSession = (): Promise<CognitoUserSession | null> => {
  return new Promise((resolve) => {
    try {
      const user = getUserPool().getCurrentUser();
      if (!user) return resolve(null);
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) return resolve(null);
        resolve(session);
      });
    } catch {
      resolve(null);
    }
  });
};

export const getAccessToken = async (): Promise<string | null> => {
  const session = await getAccountSession();
  return session?.getAccessToken().getJwtToken() ?? null;
};

export const isAccountLoggedIn = (): boolean => {
  try {
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
    if (!clientId) return false;
    const lastAuthUser = localStorage.getItem(
      `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`
    );
    return !!lastAuthUser;
  } catch {
    return false;
  }
};

export const getAccountEmail = (): string | null => {
  try {
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
    if (!clientId) return null;
    const lastAuthUser = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`);
    if (!lastAuthUser) return null;
    const idToken = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.idToken`);
    if (idToken) {
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      if (payload.email) return payload.email;
    }
    return lastAuthUser;
  } catch {
    return null;
  }
};

export const changeAccountPassword = (currentPassword: string, newPassword: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const pool = getUserPool();
    const user = pool.getCurrentUser();
    if (!user) return reject(new Error('Not logged in'));
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) return reject(new Error('Session expired. Please log in again.'));
      user.changePassword(currentPassword, newPassword, (changeErr) => {
        if (changeErr) reject(changeErr);
        else resolve();
      });
    });
  });
};

export const accountForgotPassword = (email: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    getCognitoUser(email).forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
};

export const accountConfirmForgotPassword = (
  email: string,
  code: string,
  newPassword: string
): Promise<void> => {
  return new Promise((resolve, reject) => {
    getCognitoUser(email).confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
};
